import * as types from "../mutation-types";
import { roundNumber } from "@/util";
function initialState() {
    return {
        includeRebalance: false,
        rebalanceType: 0,
        frequencyType: 12,
        threshold: 0,
        rebalanceSecurities: []
    };
}

const state = {
    ...initialState(),
    rebalanceOptions: [
        {
            value: 0,
            labelId: "rebalancing_initial_allocation"
        },
        {
            value: 1,
            labelId: "rebalancing_custom"
        }
    ],
    frequencyOptions: [
        {
            value: 1,
            labelId: "common_frequency_monthly"
        },
        {
            value: 3,
            labelId: "common_frequency_quarterly"
        },
        {
            value: 6,
            labelId: "common_frequency_semi_annually"
        },
        {
            value: 12,
            labelId: "common_frequency_annually"
        },
        {
            value: 24,
            labelId: "common_frequency_biennially"
        }
    ]
};

const getters = {
    rebalanceSetting: (state, getters, rootState) => {
        if (state.includeRebalance && rootState.settings.hypoType !== 2) {
            // disable rebalance for investments hypo.
            return {
                includeRebalance: true,
                rebalancingTo: state.rebalanceType,
                rebalanceFrequency: state.frequencyType,
                rebalanceThreshold: state.threshold
            };
        }
        return undefined;
    },
    getPreferenceRebalancingSetting: (state, getters, rootState) => {
        return rootState.settings.preferences.rebalance;
    },
    getRebalanceSecurities: (state, getters, rootState, rootGetters) => {
        let unAllocationWeight = 100;
        const securitiesWithAllocation = rootGetters["holdings/securitiesWithAllocation"];
        const isNoInitialAmount = securitiesWithAllocation.every(i => i.investAmount === 0 || parseFloat(i.investPercent) === 0);
        const investmentSecurities = securitiesWithAllocation.filter(i => i.investPercent !== 0);
        return securitiesWithAllocation.map((i, index, arr) => {
            let currentWeight;
            if (isNoInitialAmount) {
                //equal weight when initial amount is 0 and user doesn't input initial percentage.
                currentWeight = roundNumber(100 / arr.length);
                //adjust weight of the last security sum up to 100%
                if (index === arr.length - 1) {
                    currentWeight = unAllocationWeight;
                    unAllocationWeight = 0;
                }
            } else {
                //adjust weight of the last security with investment sum up to 100%
                currentWeight = roundNumber(i.investPercent);
                const lastInvestmentSecurity = investmentSecurities[investmentSecurities.length - 1];
                if (lastInvestmentSecurity.securityId === i.securityId) {
                    currentWeight = unAllocationWeight;
                    unAllocationWeight = 0;
                }
            }
            if (unAllocationWeight >= currentWeight) {
                unAllocationWeight = roundNumber(unAllocationWeight - currentWeight);
            }
            const currentSecurityInfo = state.rebalanceSecurities.find(item => item.securityId === i.securityId);
            let customWeight;
            if (currentSecurityInfo) {
                customWeight = currentSecurityInfo.customWeight;
            } else {
                const rebalancePercent = i.rebalancePercent; // saved rebalance weight
                if (rebalancePercent !== undefined) {
                    customWeight = roundNumber(rebalancePercent);
                } else {
                    customWeight = currentWeight;
                }
            }

            return {
                securityId: i.securityId,
                securityName: i.securityName,
                identifier: i.identifier,
                assetAllocation: i.assetAllocation,
                initialWeight: currentWeight,
                customWeight: customWeight
            };
        });
    },
    validateRebalanceSecuritiesTotalWeight: (state, getters) => {
        const totalWeight = getters.getRebalanceSecurities.reduce((prev, item) => {
            prev = roundNumber(prev + item.customWeight);
            return prev;
        }, 0);
        if (totalWeight === 100) {
            return {
                valid: true,
                totalWeight: totalWeight
            };
        }
        return {
            valid: false,
            totalWeight: totalWeight
        };
    }
};

const mutations = {
    [types.SET_REBALANCE_SECURITIES](state, data) {
        state.rebalanceSecurities = data;
    },
    [types.SET_INCLUDE_REBALANCE](state, data) {
        state.includeRebalance = data;
    },
    [types.SET_REBALANCE_TYPE](state, data) {
        state.rebalanceType = data;
    },
    [types.SET_FREQUENCY_TYPE](state, data) {
        state.frequencyType = data;
    },
    [types.SET_THRESHOLD](state, data) {
        state.threshold = data;
    },
    [types.SET_REBALANCE_SETTING](state, data) {
        if (data) {
            state.includeRebalance = data.includeRebalance;
            state.rebalanceType = data.rebalancingTo;
            state.frequencyType = data.rebalanceFrequency;
            state.threshold = data.rebalanceThreshold;
        } else {
            state.includeRebalance = false;
        }
    },
    [types.RESET](state, data) {
        const initialData = initialState();
        Object.keys(initialData).forEach(key => {
            state[key] = initialData[key];
        });
    }
};

const actions = {
    updateRebalanceSecurities({ dispatch, state, rootState, commit }) {
        const currentSecurities = rootState.holdings.securities;
        if (currentSecurities.some(item => state.rebalanceSecurities.find(i => i.securityId === item.securityId))) {
            const customWeightMap = {};
            state.rebalanceSecurities.forEach(item => {
                customWeightMap[item.securityId] = item.customWeight;
            });
            const securitiesWithCustomWeight = currentSecurities.map(item => {
                return {
                    ...item,
                    customWeight: typeof customWeightMap[item.securityId] === "undefined" ? 0 : customWeightMap[item.securityId]
                };
            });
            commit(types.SET_REBALANCE_SECURITIES, securitiesWithCustomWeight);
        } else {
            commit(types.SET_REBALANCE_SECURITIES, []);
        }
    }
};

export default {
    state,
    getters,
    mutations,
    actions
};
